// Agent Store - Estado del agente usando Zustand
import { create } from 'zustand';
import { agentClient } from '../lib/agentClient';
import type { AgentStatus } from '../lib/agentClient';

// intervalId fuera del store para evitar re-renders innecesarios
let autoCheckIntervalId: ReturnType<typeof setInterval> | null = null;
// [H-FE001] Flag para evitar health checks concurrentes (si uno tarda >30s, no disparar otro)
let isCheckInFlight = false;
// Contador de fallos consecutivos para backoff del health check
let consecutiveFailures = 0;

// Intervalos de polling optimizados por estado
const INITIAL_CHECK_INTERVAL = 5000;   // 5s cuando nunca se conecto (buscando agente)
const BASE_CHECK_INTERVAL = 15000;     // 15s cuando conectado (detecta desconexion rapido)
const RECONNECT_INTERVAL = 8000;       // 8s cuando se perdio conexion (reconexion rapida)
const BACKOFF_CHECK_INTERVAL = 30000;  // 30s tras muchos fallos (evitar bombardeo)

interface AgentStore {
  status: AgentStatus;
  isChecking: boolean;
  wasEverConnected: boolean;
  installedApps: { name: string; path: string }[];
  installedAppsLoading: boolean;
  installedAppsError: string | null;
  checkStatus: () => Promise<void>;
  fetchInstalledApps: () => Promise<void>;
  startAutoCheck: () => void;
  stopAutoCheck: () => void;
}

function _setPollingInterval(get: () => AgentStore, intervalMs: number) {
  if (autoCheckIntervalId) {
    clearInterval(autoCheckIntervalId);
  }
  autoCheckIntervalId = setInterval(() => {
    get().checkStatus();
  }, intervalMs);
}

export const useAgentStore = create<AgentStore>((set, get) => ({
  status: { status: 'disconnected' },
  isChecking: false,
  wasEverConnected: false,
  installedApps: [],
  installedAppsLoading: false,
  installedAppsError: null,

  checkStatus: async () => {
    // [H-FE001] Si ya hay un check en vuelo, saltar este poll
    if (isCheckInFlight) return;
    isCheckInFlight = true;
    set({ isChecking: true });

    const prevStatus = get().status.status;
    const prevWasEver = get().wasEverConnected;
    try {
      const status = await agentClient.checkStatus();
      set({ status });

      // Resetear fallos al conectar exitosamente
      if (status.status === 'connected' || status.status === 'degraded') {
        consecutiveFailures = 0;

        if (!prevWasEver) {
          // Primera conexion exitosa: probe si es agente legacy (solo 1 vez)
          const isLegacy = await agentClient.probeAuthRequired();
          if (isLegacy) {
            set({ status: { ...status, status: 'unauthorized', error: 'Agente legacy detectado' } });
            _setPollingInterval(get, BACKOFF_CHECK_INTERVAL);
            return; // No marcar wasEverConnected — el usuario debe actualizar
          }
          // Agente nuevo: marcar y cambiar a intervalo normal
          set({ wasEverConnected: true });
        }

        // Restaurar intervalo de monitoreo al (re)conectar
        if (prevStatus !== 'connected' && prevStatus !== 'degraded') {
          _setPollingInterval(get, BASE_CHECK_INTERVAL);
          get().fetchInstalledApps();
        }
      } else {
        consecutiveFailures++;
      }
    } catch {
      consecutiveFailures++;
    } finally {
      isCheckInFlight = false;
      set({ isChecking: false });

      // Ajustar intervalo segun fallos consecutivos
      if (autoCheckIntervalId && consecutiveFailures >= 3) {
        if (get().wasEverConnected) {
          // Estaba conectado, se perdio: reconectar rapido (8s)
          // Solo pasar a backoff largo despues de muchos intentos fallidos
          if (consecutiveFailures >= 15) {
            _setPollingInterval(get, BACKOFF_CHECK_INTERVAL);
          } else {
            _setPollingInterval(get, RECONNECT_INTERVAL);
          }
        } else if (consecutiveFailures >= 20) {
          // Nunca conecto tras ~100s de intentos: reducir para no bombardear
          _setPollingInterval(get, BACKOFF_CHECK_INTERVAL);
        }
      }
    }
  },

  fetchInstalledApps: async () => {
    set({ installedAppsLoading: true, installedAppsError: null });
    try {
      const apps = await agentClient.getInstalledApps();
      set({ installedApps: apps });
    } catch (error) {
      set({ installedAppsError: error instanceof Error ? error.message : 'Error desconocido' });
    } finally {
      set({ installedAppsLoading: false });
    }
  },

  startAutoCheck: () => {
    // Detener cualquier verificacion previa
    get().stopAutoCheck();
    consecutiveFailures = 0;

    // Verificar inmediatamente
    get().checkStatus();

    // Intervalo inicial: 5s si nunca se conecto, 30s si ya se conecto antes
    const initialInterval = get().wasEverConnected
      ? BASE_CHECK_INTERVAL
      : INITIAL_CHECK_INTERVAL;

    autoCheckIntervalId = setInterval(() => {
      get().checkStatus();
    }, initialInterval);
  },

  stopAutoCheck: () => {
    if (autoCheckIntervalId) {
      clearInterval(autoCheckIntervalId);
      autoCheckIntervalId = null;
    }
  }
}));
